import Filters from './Filters/Filters';
import expandMore from '../../../../../../assets/expand-more.svg';
import styles from './Report.module.scss';
import clsx from 'clsx';
import Graph from '../../../../components/Graph/Graph';
import TypeSelector from '../../../../components/TypeSelector/TypeSelector';
import { DASHBOARD_DATA_TYPES, dataTypeSelectorOptions } from '../../../..';
import CreditSummaryTable from '../CreditSummaryTable/CreditSummaryTable';
import CreditSummaryBar from './CreditSummaryBar/CreditSummaryBar';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import { leftPurpleArrow } from '../../../../../../assets/hostedassets';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import BureauEnquiryDrawer from './BureauEnquiryDrawer/BureauEnquiryDrawer';
import useReport from './useReport';
import { NEW_TO_CREDIT_THRESHOLD } from './CreditBureauConstants';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';

function Report(props) {
    const {
        //State
        dataType,
        isBureauEnquiryDrawerOpen,
        searchParams,
        setSelectedReport,
        resetQueryParams,
        selectedReportConfig,
        bureauType,
        directorId,
        selectedDirector,
        selectedReport,
        cibilOverviewData,
        chartsData,
        user,
        isCombinedReport,

        //Methods
        setDataType,
        openBureauEnquiryDrawer,
        closeBureauEnquiryDrawer,
        getReportTitle,
        getImageSource,
        fetchGraph,
    } = useReport(props);

    const renderTableSection = () => {
        return (
            <div className={styles.TableDataTypeSection}>
                <CreditSummaryTable assesseeOrgId={props.selectedCompany?.assesseeOrgId} />
            </div>
        );
    };

    const isNewToCredit = () => {
        const minScore = cibilOverviewData?.creditScoreMin;
        const maxScore = cibilOverviewData?.creditScoreMax;
        const primaryScore = maxScore || minScore;
        return typeof primaryScore == 'number' && primaryScore < NEW_TO_CREDIT_THRESHOLD;
    };

    const renderChartsSection = () => {
        return (
            <div className={styles.ChartDataTypeSection}>
                <div className={styles.Section}>
                    <div className={styles.TopCard}>
                        <div className={styles.ChartsPanel}>
                            <div className={styles.Card}>
                                <div className={styles.Metrics}>
                                    <div>
                                        Total active loan accounts
                                        <div className={clsx(styles.MetricValue, styles.GrayPill)}>
                                            {cibilOverviewData?.totalActiveAccounts}
                                        </div>
                                    </div>
                                    <div>
                                        Total sanction amount (as of today)
                                        <div className={clsx(styles.MetricValue, styles.GrayPill)}>
                                            ₹
                                            {cibilOverviewData?.totalSanctionedAmount?.toLocaleString('en-IN', {
                                                maximumFractionDigits: 2,
                                            })}
                                            L
                                        </div>
                                    </div>
                                    <div>
                                        Credit utilisation (as of today)
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.creditUtilisation || 0) < 30,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.creditUtilisation || 0) >= 30,
                                            })}
                                        >
                                            {Math.round(parseFloat(`${cibilOverviewData?.creditUtilisation}`))}%
                                        </div>
                                    </div>
                                    <div>
                                        Enquiries in past 90 days
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.enquiriesInPast90Days || 0) < 10,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.enquiriesInPast90Days || 0) >= 10,
                                            })}
                                        >
                                            {cibilOverviewData?.enquiriesInPast90Days} enquiries
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.Card}>
                                <div className={styles.Metrics}>
                                    <div>
                                        No. of accounts with DPDs
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.noOfDpdAccounts || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.noOfDpdAccounts || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.noOfDpdAccounts} accounts
                                        </div>
                                    </div>
                                    <div>
                                        Total no. of DPDs recorded
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.totalDpdsRecorded || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.totalDpdsRecorded || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.totalDpdsRecorded} days
                                        </div>
                                    </div>
                                    <div>
                                        Current overdue amount
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.totalOverdueAmount || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.totalOverdueAmount || 0) > 0,
                                            })}
                                        >
                                            ₹
                                            {cibilOverviewData?.totalOverdueAmount?.toLocaleString('en-IN', {
                                                maximumFractionDigits: 2,
                                            })}
                                            L
                                        </div>
                                    </div>
                                    <div>
                                        Highest DPD recorded till date (no. of days)
                                        <div
                                            className={clsx(styles.MetricValue, {
                                                [styles.LowRiskBadge]: (cibilOverviewData?.highestDpdRecorded || 0) <= 0,
                                                [styles.HighRiskBadge]: (cibilOverviewData?.highestDpdRecorded || 0) > 0,
                                            })}
                                        >
                                            {cibilOverviewData?.highestDpdRecorded} days
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.ChartSections}>
                    {selectedReportConfig?.chartPanel?.sections?.map((section, i) => (
                        <div className={styles.ChartSection} key={`${section.title}-${i}`}>
                            {/* <div className={styles.SectionTitle}>{section.title}</div> */}
                            <div className={styles.GraphsContainer}>
                                {section?.cards?.map((chartName, i) => (
                                    <div
                                        key={`${chartName}-${i}`}
                                        style={{
                                            gridColumn: chartsData?.[chartName]?.metadata?.tileSize === 2 ? '1/3' : '',
                                        }}
                                    >
                                        {!!chartsData?.[chartName] && (
                                            <Graph
                                                key={`${chartName}-${i}`}
                                                graphData={chartsData[chartName]}
                                                graphName={chartName}
                                                fetchGraph={() => fetchGraph(chartName)}
                                                selectedPeriod={''}
                                                user={user}
                                                showAlerts={true}
                                                horizontalChart={chartsData?.[chartName]?.metadata?.tileSize === 2}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getRenderComponent = () => {
        switch (dataType) {
            case DASHBOARD_DATA_TYPES.CHARTS:
                return <>{renderChartsSection()}</>;
            case DASHBOARD_DATA_TYPES.TABLE:
                return <>{renderTableSection()}</>;
            default:
                return 'no';
        }
    };

    return (
        <div className={styles.Report}>
            <div className={styles.Breadcrumb}>
                <div>
                    <span
                        className={styles.Blue}
                        onClick={() => {
                            setSelectedReport('');
                            resetQueryParams();
                        }}
                    >
                        <img src={leftPurpleArrow} alt="" />
                        Credit Summary
                    </span>
                    <img src={expandMore} alt="exp" height="10px" />
                    {getReportTitle()}
                </div>
                <div>
                    <CreditSummaryBar
                        minScore={cibilOverviewData?.creditScoreMin}
                        maxScore={cibilOverviewData?.creditScoreMax}
                        selectedReport={selectedReport}
                    />
                    <img src={getImageSource(selectedDirector, searchParams.get('directorBureauType'))} alt="directorBureauType" />
                </div>
            </div>

            <div className={styles.TypeSelector}>
                <TypeSelector
                    options={dataTypeSelectorOptions}
                    selectedOptionId={dataType}
                    changeSelectedOption={(id) => setDataType(id)}
                />
                {!isCombinedReport && (
                    <div className={styles.BureauEnquiry} onClick={openBureauEnquiryDrawer}>
                        <span>
                            <TopRightArrowIcon />
                        </span>
                        View Bureau Enquiry
                    </div>
                )}
            </div>

            <AicaOverview
                module="cibilIndividual"
                style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}
                resourceId={searchParams.get('directorId') as string}
            />
            {getRenderComponent()}

            {isBureauEnquiryDrawerOpen && <BureauEnquiryDrawer onClose={closeBureauEnquiryDrawer} />}
        </div>
    );
}

export default Report;
